import { Badge } from "@/shared/ui/badge";
import { FileText, Image as ImageIcon } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { EvidenceThumbnails } from "./EvidenceThumbnails";
import {
  cleanFieldTitle,
  getFieldAnswerText,
  getFieldEvidenceUrls,
  type LeafField,
} from "./searchHelpers";

// Shown when a question has NO answer in prod_data (distinct from a real "N/A"
// answer, which is displayed as-is).
function ProcessingValue() {
  return <span className="text-muted-foreground/50 italic">PROCESSING</span>;
}

// "Blank" = truly empty only. A literal "N/A"/"n/a" is a REAL answer (the
// surveyor/reviewer chose it) and is shown as-is — only a missing/empty value
// renders as PROCESSING.
function isBlankValue(value: string): boolean {
  return value.trim() === "";
}

// Conservative, exact-match coloring for status-like values. Only full values
// (not phrases) are colored to avoid mislabelling free text.
const POSITIVE_VALUES = new Set([
  "yes", "present", "compliant", "operational", "functional", "good",
  "pass", "passed", "installed", "complete", "completed", "adequate", "ok",
]);
const NEGATIVE_VALUES = new Set([
  "no", "none", "missing", "absent", "non-compliant", "noncompliant",
  "fail", "failed", "damaged", "poor", "broken", "inoperable",
]);

function getValueTone(value: string): "positive" | "negative" | null {
  const normalized = value.trim().toLowerCase();
  if (POSITIVE_VALUES.has(normalized)) return "positive";
  if (NEGATIVE_VALUES.has(normalized)) return "negative";
  return null;
}

function renderValue(
  value: string | number | boolean | null | undefined
): React.ReactNode {
  if (value === null || value === undefined) {
    return <ProcessingValue />;
  }
  if (typeof value === "boolean") {
    return (
      <Badge
        variant="outline"
        className={cn(
          "text-sm",
          value
            ? "border-green-500/50 text-green-400"
            : "border-red-500/50 text-red-400"
        )}
      >
        {value ? "Yes" : "No"}
      </Badge>
    );
  }
  if (typeof value === "string") {
    if (isBlankValue(value)) {
      return <ProcessingValue />;
    }
    const tone = getValueTone(value);
    if (tone) {
      return (
        <Badge
          variant="outline"
          className={cn(
            "text-sm",
            tone === "positive"
              ? "border-green-500/50 text-green-400"
              : "border-red-500/50 text-red-400"
          )}
        >
          {value}
        </Badge>
      );
    }
  }
  return <>{String(value)}</>;
}

function FieldRow({
  id,
  title,
  evidenceUrls,
  answerText,
  children,
}: {
  id?: string;
  title: string;
  evidenceUrls: string[];
  answerText: string;
  children: React.ReactNode;
}) {
  const cleanTitle = cleanFieldTitle(title);

  return (
    <div id={id} className="flex min-h-[64px] items-center justify-between gap-6 -mx-2 px-2 py-2 rounded-md border-b border-border/25 last:border-b-0 hover:bg-accent/15 transition-colors scroll-mt-24">
      <span className="self-center text-base leading-6 text-muted-foreground">{cleanTitle}</span>
      <div className="flex min-w-0 flex-1 items-center justify-end gap-3 text-right">
        <EvidenceThumbnails urls={evidenceUrls} title={cleanTitle} answer={answerText} />
        <span className="min-w-0 self-center text-base font-medium leading-6 text-foreground">
          {children}
        </span>
      </div>
    </div>
  );
}

function KeyValueField({
  id,
  title,
  value,
  evidenceUrls,
  answerText,
}: {
  id?: string;
  title: string;
  value: string | number | boolean | null | undefined;
  evidenceUrls: string[];
  answerText: string;
}) {
  return (
    <FieldRow id={id} title={title} evidenceUrls={evidenceUrls} answerText={answerText}>
      {renderValue(value)}
    </FieldRow>
  );
}

function MultiSelectFieldDisplay({
  id,
  title,
  values,
  evidenceUrls,
  answerText,
}: {
  id?: string;
  title: string;
  values: string[] | null;
  evidenceUrls: string[];
  answerText: string;
}) {
  const safeValues = (values || []).filter((v) => !isBlankValue(v));

  return (
    <FieldRow id={id} title={title} evidenceUrls={evidenceUrls} answerText={answerText}>
      <div className="flex flex-wrap gap-1.5 justify-end items-center">
        {safeValues.length === 0 ? (
          <ProcessingValue />
        ) : (
          safeValues.map((v, i) => (
            <Badge key={i} variant="secondary" className="text-sm">
              {v}
            </Badge>
          ))
        )}
      </div>
    </FieldRow>
  );
}

function PhotosFieldDisplay({
  id,
  title,
  urls,
  answerText,
}: {
  id?: string;
  title: string;
  urls: string[];
  answerText: string;
}) {
  const cleanTitle = cleanFieldTitle(title);

  if (urls.length === 0) {
    return (
      <div id={id} className="-mx-2 px-2 py-3 border-b border-border/25 last:border-b-0 scroll-mt-24">
        <span className="text-base text-muted-foreground">{cleanTitle}</span>
        <p className="text-sm text-muted-foreground/50 italic mt-1">
          No photos
        </p>
      </div>
    );
  }

  return (
    <FieldRow id={id} title={title} evidenceUrls={urls} answerText={answerText}>
      <span className="inline-flex items-center gap-2">
        <ImageIcon className="h-4 w-4 text-muted-foreground" />
        <Badge variant="outline" className="text-sm">
          {urls.length}
        </Badge>
      </span>
    </FieldRow>
  );
}

function AreaFieldDisplay({
  id,
  title,
  html,
  evidenceUrls,
  answerText,
}: {
  id?: string;
  title: string;
  html: string | null;
  evidenceUrls: string[];
  answerText: string;
}) {
  const cleanTitle = cleanFieldTitle(title);

  if (!html) {
    return (
      <FieldRow id={id} title={title} evidenceUrls={evidenceUrls} answerText={answerText}>
        <ProcessingValue />
      </FieldRow>
    );
  }

  return (
    <div id={id} className="-mx-2 px-2 py-3 border-b border-border/25 last:border-b-0 scroll-mt-24">
      <div className="flex items-center gap-1.5 mb-2">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <span className="text-base text-muted-foreground">{cleanTitle}</span>
      </div>
      <div className="flex items-start justify-end gap-3">
        <EvidenceThumbnails urls={evidenceUrls} title={cleanTitle} answer={answerText} />
        <div
          className="min-w-0 flex-1 text-base text-foreground bg-accent/20 rounded-lg p-3 prose prose-base prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}

export function SimpleField({ field, id }: { field: LeafField; id?: string }) {
  const evidenceUrls = getFieldEvidenceUrls(field);
  const answerText = getFieldAnswerText(field);

  if (field.type === "photos") {
    return <PhotosFieldDisplay id={id} title={field.title} urls={evidenceUrls} answerText={answerText} />;
  }
  if (field.type === "multiSelect") {
    return (
      <MultiSelectFieldDisplay
        id={id}
        title={field.title}
        values={field.value}
        evidenceUrls={evidenceUrls}
        answerText={answerText}
      />
    );
  }
  if (field.type === "area") {
    return (
      <AreaFieldDisplay
        id={id}
        title={field.title}
        html={field.value}
        evidenceUrls={evidenceUrls}
        answerText={answerText}
      />
    );
  }
  // input or select
  return (
    <KeyValueField
      id={id}
      title={field.title}
      value={field.value}
      evidenceUrls={evidenceUrls}
      answerText={answerText}
    />
  );
}
